import type { ProxyLab } from '../../hooks/useProxyAdmin';
import {
  isKnownCredentialKind,
  LAB_NAME_PATTERN,
  PROVIDER_KEY_NAME_PATTERN,
  requiresBaseUrl,
  requiresCredential,
  usesBaseUrl,
  usesCredential,
  usesUnsupportedPaths,
} from '../../utils/middlemanRules';
import { changedFields } from './fieldChanges';

// Which fields an API uses mirrors `LabConfigInput.check_api_fields`; see
// utils/middlemanRules, which middleman's tests pin. An API the viewer doesn't
// know keeps every field, so middleman (not a stale copy here) decides.
const LAB_NAME = new RegExp(LAB_NAME_PATTERN);
const KEY_NAME = new RegExp(PROVIDER_KEY_NAME_PATTERN);

export interface LabDraft {
  name: string;
  /** A string, not LabApi: a lab may be on an API middleman added since. */
  api: string;
  base_url: string;
  credential_kind: string;
  api_key_name: string;
  high_priority_api_key_name: string;
  /** One path per line. */
  unsupported_paths: string;
  provider: string;
  is_chat: boolean;
}

export function draftFromLab(lab: ProxyLab | null): LabDraft {
  return {
    name: lab?.name ?? '',
    api: lab?.api ?? 'openai',
    base_url: lab?.base_url ?? '',
    credential_kind: lab?.credential_kind ?? '',
    api_key_name: lab?.api_key_name ?? '',
    high_priority_api_key_name: lab?.high_priority_api_key_name ?? '',
    unsupported_paths: lab?.unsupported_paths.join('\n') ?? '',
    provider: lab?.provider ?? '',
    is_chat: lab?.is_chat ?? true,
  };
}

type Built = { body: Record<string, unknown> } | { error: string };

/** The whole lab the draft describes, with fields its API can't use cleared. */
export function labFromDraft(
  draft: LabDraft
): { lab: Record<string, unknown> } | { error: string } {
  const name = draft.name.trim();
  if (!LAB_NAME.test(name))
    return {
      error:
        'Lab names use lowercase letters, digits, ".", "_" and "-", starting with a letter or digit.',
    };
  // Fields an API uses are kept; only an API the copy knows needs them is
  // required to have them (see utils/middlemanRules).
  const baseUrl = usesBaseUrl(draft.api) ? draft.base_url.trim() : '';
  if (requiresBaseUrl(draft.api) && !baseUrl)
    return { error: `A ${draft.api} lab needs a base URL.` };
  if (baseUrl) {
    if (!baseUrl.startsWith('https://'))
      return { error: 'The base URL must start with https://.' };
    if (baseUrl.endsWith('/'))
      return {
        error:
          'The base URL must not end with "/": request paths are appended to it.',
      };
  }
  const credentialKind = usesCredential(draft.api) ? draft.credential_kind : '';
  if (requiresCredential(draft.api) && credentialKind === '')
    return { error: `A ${draft.api} lab needs a credential kind.` };
  // api_key sends the named key; a kind the copy doesn't know may too.
  const usesKey =
    credentialKind === 'api_key' ||
    (credentialKind !== '' && !isKnownCredentialKind(credentialKind));
  const keyName = usesKey ? draft.api_key_name.trim() : '';
  const priorityKeyName = usesKey
    ? draft.high_priority_api_key_name.trim()
    : '';
  if (credentialKind === 'api_key' && !keyName)
    return { error: 'An api_key lab needs the name of its key.' };
  for (const key of [keyName, priorityKeyName]) {
    if (key && !KEY_NAME.test(key))
      return {
        error: `Key names use uppercase letters, digits and "_" (${key}).`,
      };
  }
  const paths = usesUnsupportedPaths(draft.api)
    ? draft.unsupported_paths
        .split('\n')
        .map(p => p.trim())
        .filter(Boolean)
    : [];
  const badPath = paths.find(p => !p.startsWith('/'));
  if (badPath)
    return { error: `Unsupported paths start with "/" (${badPath}).` };
  if (new Set(paths).size !== paths.length)
    return { error: 'Unsupported paths must not repeat.' };
  return {
    lab: {
      name,
      api: draft.api,
      base_url: baseUrl || null,
      credential_kind: credentialKind || null,
      api_key_name: keyName || null,
      high_priority_api_key_name: priorityKeyName || null,
      unsupported_paths: paths,
      provider: draft.provider || null,
      is_chat: draft.is_chat,
    },
  };
}

export function buildLabBody(draft: LabDraft, lab: ProxyLab | null): Built {
  const built = labFromDraft(draft);
  if ('error' in built) return built;
  if (!lab) return { body: built.lab };
  const { name: _name, ...fields } = built.lab;
  const patch = changedFields(
    lab as unknown as Record<string, unknown>,
    fields
  );
  if (Object.keys(patch).length === 0) return { error: 'Nothing changed.' };
  return { body: patch };
}
